import React from "react";
import {UserPermissionEnum} from "../../../utils/types/user";

import {
  CompareDateLabels,
  hasMorePages,
  ReportProps,
  useServerSort,
} from "./Report.common";
import {
  ReportAdmission,
  ReportsType,
  SortField,
} from "../../../store/reporting-reports/reporting-reports.common";
import {CardTitle} from "../../../atoms/card-title";
import {RequiredUserPermission} from "../../../atoms/admin-user-permissions/RequiredUserPermission";
import {ReportingContextReports} from "../ReportingContextReports";
import {withCompare} from "./helpers";
import {LoadMoreButton} from "../atoms/load-more-button";
import {
  Column,
  ListTableReporting,
} from "../../../atoms/admin-reporting/list-table-reporting";
import {
  DateReportsCell,
  TextCellEllipsis,
} from "../../../atoms/list-table/SharedCells";
import {formatCurrency} from "../../../utils/helpers/string";
import {readableRole} from "../../../atoms/list-table/helpers";

export const SNFABN_COST_ADMISSION_REPORT_ID = "snfabn-cost-admission" as const;

export const ReportSNFABNEstimatedCostAdmission = (props: ReportProps) => {
  const [sortDirection, sortField, setSort] = useServerSort(
    SortField.facilityName,
  );

  return (
    <>
      <div className="chart-header">
        <CardTitle
          title="SNFABN – Run report “Estimated Cost” by facility - User role – track last time “Estimated Cost” was updated"
          subtitle="by Facility"
        />
        <CompareDateLabels dateRange={props.dateRange} />
      </div>

      <RequiredUserPermission
        permission={UserPermissionEnum.ESTIMATED_COST_TRACKED_BY_ROLE_AND_DATE}
      >
        <ReportingContextReports
          dataPaths={withCompare({
            type: ReportsType.SNFABNEstimatedCostAdmission,
            query: {
              from: props.dateRange.from,
              to: props.dateRange.to,
              sortDirection: sortDirection,
              sortField: sortField,
              facilities: props.facilities,
            },
          })}
          enabled={props.reportingBlockSetters.enabled}
          onDataPathChange={
            props.reportingBlockSetters
              ? props.reportingBlockSetters.setExportDataPath
              : undefined
          }
        >
          {(d, loadMore) => (
            <>
              <ListTableReporting
                data={d}
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={setSort}
                noRowsMessage="No Admissions found with these parameters..."
                full={props.reportingBlockSetters.isFullReport}
              >
                <Column<ReportAdmission>
                  sortKey={SortField.residentName}
                  cellRenderer={row => (
                    <TextCellEllipsis small>
                      {row.rowData.residentName}
                    </TextCellEllipsis>
                  )}
                  label="Resident"
                  width={14}
                />

                <Column<ReportAdmission>
                  cellRenderer={row => (
                    <TextCellEllipsis small>
                      {formatCurrency(row.rowData.estimatedCost)}
                    </TextCellEllipsis>
                  )}
                  label="Estimated Cost"
                  sortKey={SortField.estimatedCost}
                  width={14}
                />

                <Column<ReportAdmission>
                  cellRenderer={row => (
                    <TextCellEllipsis small>
                      {row.rowData.facilityName}
                    </TextCellEllipsis>
                  )}
                  label="Facility"
                  sortKey={SortField.facilityName}
                  width={14}
                />
                <Column<ReportAdmission>
                  cellRenderer={row => (
                    <DateReportsCell data={row.rowData.createdDate} />
                  )}
                  label="Created Date"
                  sortKey={SortField.createdDate}
                  width={13}
                />
                <Column<ReportAdmission>
                  cellRenderer={row => (
                    <DateReportsCell data={row.rowData.admissionDate} />
                  )}
                  label="Admission Date"
                  sortKey={SortField.admissionDate}
                  width={13}
                />

                <Column
                  cellRenderer={(row: any) => (
                    <DateReportsCell data={row.rowData.sentToResidentDate} />
                  )}
                  label="Sent to Resident Date"
                  sortKey={SortField.sentToResidentDate}
                  width={13}
                />

                <Column<ReportAdmission>
                  cellRenderer={row => (
                    <DateReportsCell data={row.rowData.estimationDate} />
                  )}
                  label="Estimation Date"
                  sortKey={SortField.updatedDate}
                  width={13}
                />

                <Column<ReportAdmission>
                  cellRenderer={row => (
                    <TextCellEllipsis small>
                      {row.rowData.estimator}
                    </TextCellEllipsis>
                  )}
                  label="Estimated By"
                  sortKey={SortField.estimator}
                  width={13}
                />
                <Column
                  cellRenderer={(row: any) => (
                    <TextCellEllipsis small>
                      {readableRole(row.rowData.estimatedBy)}
                    </TextCellEllipsis>
                  )}
                  label="Estimated By Role"
                  sortKey={SortField.estimatedBy}
                  width={13}
                />
              </ListTableReporting>

              <LoadMoreButton
                hasMorePages={hasMorePages(
                  d,
                  props.reportingBlockSetters.isFullReport,
                )}
                onClick={loadMore}
              />
            </>
          )}
        </ReportingContextReports>
      </RequiredUserPermission>
    </>
  );
};
